import React, { useMemo } from 'react';
import { View, Text, Animated, Pressable, Platform } from 'react-native';
import { Zap, BarChart2, List } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import type { getMatchCardStyles } from './MatchCard.styles';
import { MatchCardHeroShell } from './MatchCardHeroShell';
import { resolveMatchCardOutcomeStyles } from './matchCardOutcome.helper';
import type { Theme } from '../../../src/theme/getTheme';
import type { WorldCupMatch } from '../../../src/worldcup/types/worldcup.types';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';

interface MatchCardLiveProps {
  match: WorldCupMatch;
  isBoosted: boolean;
  stageBadgeText: string;
  homeRank: string | null;
  awayRank: string | null;
  cardScale: { transform: { scale: Animated.AnimatedInterpolation<number> }[] };
  liveAnim: Animated.Value;
  isLiveExactHit: boolean;
  isLiveCorrectResult: boolean;
  /** True when the badge is judged on a 90-min result that differs from the running scoreline (knockout with ET goals). */
  isLive90MinJudged: boolean;
  hasPrediction: boolean;
  hasLiveScore: boolean;
  styles: ReturnType<typeof getMatchCardStyles>;
  theme: Theme;
  predictionLabel?: string;
  onFixturePress?: (matchId: string) => void;
  onStatsPress?: (matchId: string) => void;
}

export const MatchCardLive: React.FC<MatchCardLiveProps> = ({
  match,
  isBoosted,
  stageBadgeText,
  homeRank,
  awayRank,
  cardScale,
  liveAnim,
  isLiveExactHit,
  isLiveCorrectResult,
  isLive90MinJudged,
  hasPrediction,
  hasLiveScore,
  styles,
  theme,
  predictionLabel = 'You',
  onFixturePress,
  onStatsPress,
}) => {
  const homeKitFallback = useMemo(() => getTeamKitConfig(match.homeTeam), [match.homeTeam]);
  const awayKitFallback = useMemo(() => getTeamKitConfig(match.awayTeam), [match.awayTeam]);

  const {
    cardBorder: liveCardBorder,
    accentBar: liveAccentBar,
    outcomeLabelStyle: liveOutcomeLabelStyle,
    outcomeLabel: liveOutcomeLabel,
    boostGlow: liveBoostGlow,
    boostBadgeStyle: liveBoostBadgeStyle,
    boostBadgeTextStyle: liveBoostBadgeTextStyle,
    boostZapColor: liveBoostZapColor,
  } = resolveMatchCardOutcomeStyles(isLiveExactHit, isLiveCorrectResult, isBoosted, styles, theme);

  return (
    <Animated.View style={cardScale}>
      <View style={[styles.finishedCard, liveCardBorder, liveBoostGlow]}>
        <MatchCardHeroShell />
        <View style={styles.finishedContent}>
          <View style={[styles.accentBar, liveAccentBar]} />
          <View style={styles.finishedTopRow}>
            <View style={styles.stageRow}>
              <Text style={styles.finishedStageBadge}>{stageBadgeText}</Text>
              {isBoosted && (
                <View style={[styles.boostMiniBadge, liveBoostBadgeStyle]}>
                  <Zap size={10} color={liveBoostZapColor} />
                  <Text style={[styles.boostMiniBadgeText, liveBoostBadgeTextStyle]}>x2</Text>
                </View>
              )}
            </View>
            <View style={styles.liveBadge}>
              <Animated.View style={[styles.liveDot, { opacity: liveAnim }]} />
              <Text style={styles.liveText}>
                {match.liveMinute != null ? `${match.liveMinute}'` : 'Live'}
              </Text>
            </View>
          </View>

          <View style={styles.finishedMatchRow}>
            <View style={styles.finishedTeamBlock}>
              <View style={styles.flagWrapper}>
                <CountryFlag
                  countryCode={match.homeCountryCode}
                  kitFallback={homeKitFallback}
                  accessibilityLabel={`${match.homeTeam} flag`}
                />
              </View>
              <Text style={styles.finishedTeamName} numberOfLines={1} ellipsizeMode="tail">
                {match.homeTeam}
              </Text>
              {homeRank != null && <Text style={styles.groupPosition}>{homeRank}</Text>}
            </View>
            <View style={styles.finishedScoreBlock}>
              <View style={styles.finishedScoreRow}>
                <Text style={styles.finishedScoreNum}>{match.homeScore ?? 0}</Text>
                <Text style={styles.finishedScoreDash}>–</Text>
                <Text style={styles.finishedScoreNum}>{match.awayScore ?? 0}</Text>
              </View>
            </View>
            <View style={styles.finishedTeamBlock}>
              <View style={styles.flagWrapper}>
                <CountryFlag
                  countryCode={match.awayCountryCode}
                  kitFallback={awayKitFallback}
                  accessibilityLabel={`${match.awayTeam} flag`}
                />
              </View>
              <Text style={styles.finishedTeamName} numberOfLines={1} ellipsizeMode="tail">
                {match.awayTeam}
              </Text>
              {awayRank != null && <Text style={styles.groupPosition}>{awayRank}</Text>}
            </View>
          </View>

          {hasPrediction && (
            <View style={styles.finishedFooter}>
              <View style={styles.finishedFooterLeft}>
                <Text style={styles.finishedPredictionText}>
                  {`${predictionLabel}: ${match.prediction!.home}–${match.prediction!.away}`}
                </Text>
                {hasLiveScore && (
                  <Text style={[styles.finishedOutcomeLabel, liveOutcomeLabelStyle]}>
                    {liveOutcomeLabel}
                    {isLive90MinJudged && <Text style={styles.live90MinCue}>{" (90')"}</Text>}
                  </Text>
                )}
              </View>
            </View>
          )}

          <View style={styles.bottomRow}>
            <Pressable
              onPress={() => onStatsPress?.(match.id)}
              style={({ pressed }) => [
                styles.cardActionButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.cardActionButtonPressed,
              ]}
            >
              <BarChart2 size={13} color={styles.cardActionIcon.color as string} strokeWidth={2} />
              <Text style={styles.cardActionText}>Stats</Text>
            </Pressable>

            <View style={styles.cardActionDivider} />

            <Pressable
              onPress={() => onFixturePress?.(match.id)}
              style={({ pressed }) => [
                styles.cardActionButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.cardActionButtonPressed,
              ]}
            >
              <List size={13} color={styles.cardActionIcon.color as string} strokeWidth={2} />
              <Text style={styles.cardActionText}>Match Details</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Animated.View>
  );
};
